import { useState } from "react"
import { useStripe, useElements } from "@stripe/react-stripe-js"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { api } from "aldoo-ra/API"
import PlatformAuth from "aldoo-ra/PlatformAuth"

const { useAuth } = PlatformAuth

const usePaymentSubmission = (store) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [auth, setAuth] = useAuth()
  const { onPaymentSuccess, paymentInfo, onPaymentError, customAuth } = store

  const storeAPIFunc = customAuth ? api("store", customAuth) : null
  const storeAPI = customAuth ? {
    completeSubscriptionAfterSetup: async (payload) => {
      return await storeAPIFunc({ complete_subscription_after_setup: { platform: "Web", ...payload } })
    },
    updateCustomerInvoiceDetails: async (payload) => {
      return await storeAPIFunc({ update_customer_invoice_details: { platform: "Web", ...payload } })
    },
    updateCustomerEmail: async (email) => {
      return await storeAPIFunc({ "update-customer-email": { platform: "Web", email } })
    },
  } : AldooStoreAPI

  const completeSubscriptionAfterSetup = async (setupIntentId) => {
    try {
      const response = await storeAPI.completeSubscriptionAfterSetup({
        pid: paymentInfo.pid,
        setupIntentId,
        promo_code: paymentInfo.promoCode,
      })
      if (response.error) throw new Error(response.error)

      if (response.requiresPayment && response.clientSecret) {
        const { error: paymentError } = await stripe.confirmPayment({
          clientSecret: response.clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}/payment-result`,
          },
        })

        if (paymentError) {
          throw new Error(paymentError.message)
        }
      }

      return response
    } catch (error) {
      throw error
    }
  }

  const handleSubmitWithSavedMethod = async (invoiceData) => {
    if (!stripe) return

    setLoading(true)
    try {
      if (invoiceData?.needsInvoice) {
        await storeAPI.updateCustomerInvoiceDetails({
          companyName: invoiceData.companyName,
          taxId: invoiceData.taxId,
          address: invoiceData.address,
        })
      }

      const isSetupIntent = paymentInfo.clientSecret?.startsWith("seti_")
      const isPaymentIntent = paymentInfo.clientSecret?.startsWith("pi_")
      let result

      const paymentMethodType = paymentInfo.savedPaymentMethod.type

      if (isSetupIntent) {
        const subscriptionResult = await completeSubscriptionAfterSetup(null)

        if (subscriptionResult?.error) {
          console.error('❌ Subscription creation failed:', subscriptionResult)
          throw {
            message: subscriptionResult.message || 'Subscription creation failed',
            code: subscriptionResult.error,
            decline_code: subscriptionResult.decline_code,
            payment_intent: { id: subscriptionResult.payment_intent_id }
          }
        }

        if (subscriptionResult?.paymentIntentId) {
          store.setPaymentIntentId(subscriptionResult.paymentIntentId)
        }
        if (subscriptionResult?.subscriptionId) {
          store.setSubscriptionId(subscriptionResult.subscriptionId)
        }

        result = {
          setupIntent: { status: 'succeeded', id: 'saved_method' },
          subscriptionResult
        }
      } else if (isPaymentIntent) {
        if (paymentMethodType === 'card') {
          result = await stripe.confirmCardPayment(
            paymentInfo.clientSecret,
            {
              payment_method: paymentInfo.savedPaymentMethod.id,
            }
          )
        } else {
          result = await stripe.confirmPayment({
            clientSecret: paymentInfo.clientSecret,
            confirmParams: {
              payment_method: paymentInfo.savedPaymentMethod.id,
              return_url: window.location.href,
            },
            redirect: 'if_required'
          })
        }
      } else if (paymentInfo.type === "free_subscription") {
        result = { error: null }
      } else {
        throw new Error("Unknown intent type")
      }

      if (result.error) {
        throw result.error
      }

      let capturedPaymentIntentId = null
      let capturedSubscriptionId = null

      if (result.paymentIntent) {
        capturedPaymentIntentId = result.paymentIntent.id
      } else if (result.subscriptionResult) {
        capturedPaymentIntentId = result.subscriptionResult.paymentIntentId
        capturedSubscriptionId = result.subscriptionResult.subscriptionId
      }

      onPaymentSuccess({
        paymentIntentId: capturedPaymentIntentId,
        subscriptionId: capturedSubscriptionId
      })
    } catch (error) {
      onPaymentError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const handleAnonymousPurchase = async (email) => {
    if (email) {
      try {
        await storeAPI.updateCustomerEmail(email)
      } catch (error) {
        // Don't throw - continue with payment even if email update fails
      }
    }
    return null
  }

  const handleSubmit = async (invoiceData, email) => {
    setLoading(true)
    if (!stripe) return
    if (!elements) return
    const return_url = `${window.location.origin}/payment-result`

    try {
      if (email) {
        await handleAnonymousPurchase(email)
      }

      if (invoiceData?.needsInvoice) {
        const invoiceEmail = email || auth?.email

        if (invoiceData.address && invoiceData.addressComplete) {
          await storeAPI.updateCustomerInvoiceDetails({
            companyName: invoiceData.companyName,
            taxId: invoiceData.taxId,
            address: invoiceData.address,
            email: invoiceEmail,
          })
        } else {
          const addressElement = elements.getElement('address')
          if (addressElement) {
            const { complete, value: addressValue } = await addressElement.getValue()

            if (complete && addressValue) {
              await storeAPI.updateCustomerInvoiceDetails({
                companyName: invoiceData.companyName,
                taxId: invoiceData.taxId,
                address: {
                  line1: addressValue.address.line1,
                  line2: addressValue.address.line2,
                  city: addressValue.address.city,
                  state: addressValue.address.state,
                  postal_code: addressValue.address.postal_code,
                  country: addressValue.address.country,
                },
                email: addressValue.email || invoiceEmail,
                name: addressValue.name || undefined,
              })
            }
          }
        }
      }

      const isSetupIntent = paymentInfo.clientSecret?.startsWith("seti_")
      const isPaymentIntent = paymentInfo.clientSecret?.startsWith("pi_")

      if (isSetupIntent || paymentInfo.type === "trial_setup") {
        const { setupIntent, error: setupError } = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
          confirmParams: {
            return_url,
          },
        })

        if (setupError) return onPaymentError(setupError)

        if (setupIntent && setupIntent.status === "succeeded") {
          const subscriptionResult = await completeSubscriptionAfterSetup(setupIntent.id)

          let capturedPaymentIntentId = null
          let capturedSubscriptionId = null

          if (subscriptionResult?.subscriptionId) {
            capturedSubscriptionId = subscriptionResult.subscriptionId
            store.setSubscriptionId(capturedSubscriptionId)
          }

          if (subscriptionResult?.paymentIntentId) {
            capturedPaymentIntentId = subscriptionResult.paymentIntentId
            store.setPaymentIntentId(capturedPaymentIntentId)
          } else if (subscriptionResult?.clientSecret) {
            capturedPaymentIntentId = subscriptionResult.clientSecret.split('_secret_')[0]
            store.setPaymentIntentId(capturedPaymentIntentId)
          }

          onPaymentSuccess({
            paymentIntentId: capturedPaymentIntentId,
            subscriptionId: capturedSubscriptionId
          })
          return
        } else {
          throw new Error('Setup intent did not succeed')
        }
      } else if (isPaymentIntent) {
        const result = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
          confirmParams: {
            return_url,
          },
        })

        if (result.error) {
          return onPaymentError(result.error)
        }

        if (result.paymentIntent) {
          const actualPaymentIntentId = result.paymentIntent.id
          store.setPaymentIntentId(actualPaymentIntentId)
          onPaymentSuccess({
            paymentIntentId: actualPaymentIntentId
          })
          return
        }
      } else {
        throw new Error("Unknown intent type: unable to determine from clientSecret")
      }

      onPaymentSuccess()
    } catch (err) {
      console.error('❌ Payment submission error:', err)
      onPaymentError(err)
    } finally {
      setLoading(false)
    }
  }

  return {
    handleSubmit,
    handleSubmitWithSavedMethod,
    loading,
    setLoading,
    ready,
    setReady,
    stripe,
    elements,
    isReady: stripe && ready && !loading,
  }
}

export default usePaymentSubmission
